import {
  CollectorDNSRecord,
  DNSSetup,
} from '@debbie/common/dist/collectors/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as collectorsAPI from '@debbie/cortex/dist/api/collectors.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { Container } from '@debbie/iris/dist/components/Grid/index.js'
import { ArrowRight } from '@debbie/iris/dist/components/Icons/index.js'
import {
  Button,
  CopyToClipboard,
  Panel,
  PlaceholderLoader,
  Table,
} from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import logo from '../assets/images/logoneg.svg'

const CollectorDNSSetup = () => {
  const lang = useSelector((s: RootState) => s.lang)
  const { tenantId, collectorId } = useParams<{
    tenantId: string
    collectorId: string
  }>()

  const [dns, setDns] = useState<{
    name: string
    verified: boolean
    setup: DNSSetup
  } | null>(null)

  const load = () =>
    collectorsAPI.dnsSetup(tenantId, collectorId).then((res) => {
      setDns(res)
    })

  const checkDNS = useMutation(load, {
    onError: () => {
      alert('Error checking DNS')
    },
  })

  useEffect(() => {
    if (!dns) {
      load()
    }
  }, [])

  return (
    <Page>
      <Container style={{ width: '900px' }}>
        <br />
        <img src={logo} className="logo" alt={lang.misc.logo} width="160px" />
        {dns && !dns.verified && (
          <Button
            onClick={() => {
              setDns(null)
              checkDNS.mutate()
            }}
            loading={checkDNS.isLoading}
            style={{ float: 'right' }}
          >
            {lang.dnsSetup.checkDns}
          </Button>
        )}
        <h1>
          {lang.dnsSetup.title}
          {dns ? ` - ${dns.name}` : null}
        </h1>
        {dns?.verified && (
          <Panel bodyStyle={{ backgroundColor: THEME.SUCCESS, color: 'white' }}>
            {lang.dnsSetup.allRecordsConfirmed}
          </Panel>
        )}
        <Panel>
          {dns != null ? (
            <div>
              <h2>{lang.dnsSetup.domainVerification}</h2>
              {dns.setup.records
                .filter(
                  (record) => record.description === 'domain-verification',
                )
                .map((record) => (
                  <DNSRecord record={record} />
                ))}
              <h2>{lang.dnsSetup.email}</h2>
              <h3>{lang.dnsSetup.outboundEmails}</h3>
              <p>
                {lang.dnsSetup.spfExistingPrefix}{' '}
                <code>
                  {
                    dns.setup.records.find((record) =>
                      ['e-mail-spf'].includes(record.description),
                    )?.raw
                  }
                </code>{' '}
                {lang.dnsSetup.spfExistingSuffix}
              </p>
              {dns.setup.records
                .filter((record) =>
                  ['e-mail-spf', 'e-mail-dkim'].includes(record.description),
                )
                .map((record) => (
                  <DNSRecord record={record} />
                ))}
              <h3>{lang.dnsSetup.inboundEmails}</h3>
              <p>{lang.dnsSetup.inboundForwarded}</p>
              <p>
                <b>
                  <CopyToClipboard text={dns.setup.outboundEmail}>
                    {dns.setup.outboundEmail}
                  </CopyToClipboard>{' '}
                  <ArrowRight style={{ margin: '0.2em 0 -0.2em' }} />{' '}
                  <CopyToClipboard text={dns.setup.inboundEmail}>
                    {dns.setup.inboundEmail}
                  </CopyToClipboard>
                </b>
              </p>

              {(dns.setup.creditorWebBase || dns.setup.debtorWebBase) && (
                <>
                  <h2>{lang.dnsSetup.sites}</h2>
                  <h3>{lang.dnsSetup.debtorWebApp}</h3>
                  {dns.setup.records
                    .filter((record) => 'debtor-webapp' === record.description)
                    .map((record) => (
                      <DNSRecord record={record} />
                    ))}
                  <h3>{lang.dnsSetup.creditorWebApp}</h3>
                  {dns.setup.records
                    .filter(
                      (record) => 'creditor-webapp' === record.description,
                    )
                    .map((record) => (
                      <DNSRecord record={record} />
                    ))}
                </>
              )}
            </div>
          ) : (
            <>
              <br />
              <br />
              <PlaceholderLoader />
            </>
          )}
        </Panel>
      </Container>
    </Page>
  )
}

export default CollectorDNSSetup

const DNSRecord = (props: { record: CollectorDNSRecord }) => {
  const { record } = props
  const lang = useSelector((s: RootState) => s.lang)

  return (
    <DNSWrapper>
      <Table>
        <tr>
          <th style={{ width: '80px' }}>{lang.dnsSetup.verified}</th>
          <th style={{ width: '100px' }}>{lang.dnsSetup.type}</th>
          <th>{lang.dnsSetup.hostname}</th>
          <th style={{ width: '420px' }}>{lang.dnsSetup.value}</th>
        </tr>
        <tr>
          <td style={{ textAlign: 'center' }}>
            {record.verified ? '✅' : '❌'}
          </td>
          <td>
            <b>{record.type}</b>
          </td>
          <td>
            <CopyToClipboard text={record.name}>{record.name}</CopyToClipboard>
          </td>
          <td>
            <CopyToClipboard
              style={{
                width: '400px',
              }}
              text={record.value}
            >
              {record.value}
            </CopyToClipboard>
            {record.currentValue && (
              <>
                <br />
                <br />
                <div style={{ fontSize: '0.8em' }}>
                  <b>{lang.dnsSetup.currentValue}</b>
                  <br />
                  {record.currentValue}
                </div>
              </>
            )}
          </td>
        </tr>
      </Table>
    </DNSWrapper>
  )
}

const DNSWrapper = styled.div`
  padding: 0 0 40px 0;
  overflow-wrap: break-word;
`

const Page = styled.div`
  overflow-y: auto;
  height: 100vh;
  padding-bottom: 100px;
  background-color: #f1f4fb;
`
